import React, { useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { DateTime } from 'luxon'
import { useSnackbar } from 'notistack'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import IconButton from '@material-ui/core/IconButton'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import AddOutlinedIcon from 'components/material-symbol/icons/Add'
import CloseOutlinedIcon from 'components/material-symbol/icons/Close'
import EventIcon from 'components/material-symbol/icons/Event'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import TipWell from 'components/tip-well'
import {
  DesktopDatePicker,
  DesktopTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers'
import {
  enterTimeViewRenderers,
  mergeEnterTimePickerSlotProps,
  ENTER_TIME_FIELD_FORMAT,
} from 'components/pga-time-picker/enter-time-view'
import {
  mergeDesktopDatePickerSlotProps,
  mergeDesktopDatePickerSlots,
} from 'components/pga-time-picker/desktop-date-picker'
import { ThemeProvider } from '@mui/material/styles'
import { makeStyles } from '@material-ui/core/styles'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import CustomLuxonUtils from 'lib/CustomLuxonUtils'
import {
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_BORDER,
  OUTLINED_INPUT_BORDER_RADIUS,
  getOutlinedSelectMenuPaperStyles,
  getOutlinedSelectWrapperStyles,
  getOutlinedTextFieldWrapperStyles,
  outlinedPickerFieldSx,
} from 'lib/form-field-styles'
import { calendarDatePickerTheme } from 'pages/pga-coach/bookings/schedule/calendar/calendar-date-picker-theme'
import { SET_COACH_AVAILABILITY_OVERRIDE } from '../custom-availability/queries'
import { findOpenTimeOverride, mergeOpenTimeSlotsForDate } from './utils'

const PICKER_ICON_COLOR = '#172024'

const DatePickerIcon = props => (
  <EventIcon fill={0} {...props} htmlColor={PICKER_ICON_COLOR} />
)

const TimePickerIcon = props => (
  <ScheduleIcon fill={0} {...props} htmlColor={PICKER_ICON_COLOR} />
)

const CREATE_BLOCK_TIME = gql`
  mutation CreateCoachAvailabilityBlockTime(
    $input: CreateCoachAvailabilityBlockTimeInput!
  ) {
    createCoachAvailabilityBlockTime(input: $input) {
      success
      message
    }
  }
`

const useStyles = makeStyles(theme => ({
  sectionLabel: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: CALLOUT_FOREGROUND,
    marginBottom: theme.spacing(1.5),
  },
  tip: {
    marginBottom: theme.spacing(3),
  },
  facilityField: {
    ...getOutlinedSelectWrapperStyles(theme),
    ...getOutlinedTextFieldWrapperStyles(theme),
    '& .MuiInputLabel-outlined': {
      display: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      display: 'none',
    },
  },
  facilityMenuPaper: getOutlinedSelectMenuPaperStyles(theme),
  noteField: {
    ...getOutlinedTextFieldWrapperStyles(theme),
  },
  rangeBlock: {
    marginBottom: theme.spacing(2.5),
    '&:last-of-type': {
      marginBottom: theme.spacing(1),
    },
  },
  dateRow: {
    marginBottom: theme.spacing(1.5),
  },
  timeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  timeField: {
    flex: 1,
    minWidth: 0,
  },
  timeRangeRemoveSlot: {
    flex: '0 0 40px',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 8,
  },
  addRangeButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    paddingLeft: 0,
    textTransform: 'none',
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(1),
  },
  cancelAction: {
    textTransform: 'none',
    fontWeight: 600,
    color: CALLOUT_FOREGROUND,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  saveAction: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 24,
    padding: theme.spacing(1, 2.5),
    minWidth: 'auto',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
}))

const pickerPopperSx = {
  '& .MuiPaper-root': {
    borderRadius: `${OUTLINED_INPUT_BORDER_RADIUS}px`,
    border: `1px solid ${OUTLINED_INPUT_BORDER}`,
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.12)',
  },
}

const blankRange = (date = null) => ({
  date,
  startTime: null,
  endTime: null,
})

const toValidOrNull = val => (val?.isValid ? val : null)

const isValidDateTime = val => Boolean(val?.isValid)

const isInvalidRange = r =>
  isValidDateTime(r.startTime) &&
  isValidDateTime(r.endTime) &&
  r.endTime <= r.startTime

const isPastDate = d =>
  isValidDateTime(d) && d.startOf('day') < DateTime.local().startOf('day')

const formatTime = time => time.toFormat('h:mm a')

const formatRangeLabel = r => {
  if (!r.date) return null
  const datePart = r.date.toFormat('MMM d, yyyy')
  if (r.startTime && r.endTime) {
    return `${datePart}, ${formatTime(r.startTime)} – ${formatTime(r.endTime)}`
  }
  return datePart
}

const joinLabels = labels => {
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join('; ')}; and ${labels[labels.length - 1]}`
}

const formatBlockingTip = ranges => {
  const labels = ranges.map(formatRangeLabel).filter(Boolean)
  if (labels.length === 0) {
    return "Students won't be able to book sessions with you during a blocked time."
  }
  if (labels.length === 1) {
    return `You're blocking ${labels[0]}. Students won't be able to book sessions with you during this time.`
  }
  return `You're blocking ${joinLabels(
    labels,
  )}. Students won't be able to book sessions with you during these times.`
}

const OneTimeSlotForm = ({
  facilities,
  onSuccess,
  onCancel,
  initialType = 'open',
  defaultFacilityId,
  defaultDate,
  defaultStartTime,
  existingOverrides,
}) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()

  const type = initialType
  const isBlock = type === 'block'
  const [selectedFacilityId, setSelectedFacilityId] = useState(() => {
    if (
      defaultFacilityId &&
      facilities?.some(f => f.id === defaultFacilityId)
    ) {
      return defaultFacilityId
    }
    return facilities && facilities.length === 1 ? facilities[0].id : ''
  })
  const [date, setDate] = useState(
    () =>
      defaultDate ||
      (defaultStartTime ? defaultStartTime.startOf('day') : null),
  )
  const [ranges, setRanges] = useState(() => {
    const initialDate =
      defaultDate || (defaultStartTime ? defaultStartTime.startOf('day') : null)
    if (defaultStartTime) {
      const proposedEnd = defaultStartTime.plus({ hours: 1 })
      const sameDay = proposedEnd.hasSame(defaultStartTime, 'day')
      return [
        {
          date: initialDate,
          startTime: defaultStartTime,
          endTime: sameDay ? proposedEnd : null,
        },
      ]
    }
    return [blankRange(isBlock ? initialDate : null)]
  })
  const [note, setNote] = useState('')

  const [
    setCoachAvailabilityOverride,
    { loading: savingOverride },
  ] = useMutation(SET_COACH_AVAILABILITY_OVERRIDE, {
    onCompleted: data => {
      const result = data.setCoachAvailabilityOverride
      if (result.success) {
        enqueueSnackbar('Custom availability saved', { variant: 'success' })
        onSuccess && onSuccess()
      } else {
        enqueueSnackbar(result.message || 'Failed to save', {
          variant: 'error',
        })
      }
    },
    onError: () => {
      enqueueSnackbar('Failed to save. Please try again.', { variant: 'error' })
    },
  })

  const [createBlockTime, { loading: savingBlock }] = useMutation(
    CREATE_BLOCK_TIME,
  )

  const [isBatchSubmittingBlocks, setIsBatchSubmittingBlocks] = useState(false)
  const isSubmitting = savingOverride || savingBlock || isBatchSubmittingBlocks
  const hasInvalidRange = ranges.some(isInvalidRange)
  const hasIncompleteRange = ranges.some(
    r => !isValidDateTime(r.startTime) || !isValidDateTime(r.endTime),
  )
  const hasIncompleteBlockDate =
    isBlock && ranges.some(r => !isValidDateTime(r.date))
  const hasPastBlockDate = isBlock && ranges.some(r => isPastDate(r.date))

  const canSubmit =
    !hasIncompleteRange &&
    !hasInvalidRange &&
    !isSubmitting &&
    (isBlock
      ? !hasIncompleteBlockDate && !hasPastBlockDate
      : isValidDateTime(date) && !isPastDate(date) && selectedFacilityId)

  const updateRange = (idx, key, value) => {
    setRanges(prev =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
    )
  }

  const addRange = () => {
    setRanges(prev => {
      if (!isBlock) return [...prev, blankRange()]
      const lastDate = prev[prev.length - 1]?.date
      const nextDate = lastDate ? lastDate.plus({ days: 1 }) : date
      return [...prev, blankRange(nextDate)]
    })
  }

  const removeRange = idx => {
    setRanges(prev =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== idx),
    )
  }

  const onSubmit = async () => {
    if (type === 'open') {
      const dateISO = date.toISODate()
      const existingOverride = findOpenTimeOverride(
        existingOverrides,
        dateISO,
        selectedFacilityId,
      )
      const timeSlots = mergeOpenTimeSlotsForDate(
        dateISO,
        existingOverride?.timeSlots,
        ranges,
      )

      setCoachAvailabilityOverride({
        variables: {
          coachFacilityId: selectedFacilityId,
          input: {
            date: dateISO,
            timeSlots,
          },
        },
      })
      return
    }

    const merge = new CustomLuxonUtils()
    setIsBatchSubmittingBlocks(true)
    try {
      const settled = await Promise.all(
        ranges.map(r => {
          const startDateTime = merge.mergeDateAndTime(r.date, r.startTime)
          const endDateTime = merge.mergeDateAndTime(r.date, r.endTime)
          return createBlockTime({
            variables: {
              input: {
                startTime: startDateTime,
                endTime: endDateTime,
                allDay: false,
                blockNote: note,
              },
            },
          })
            .then(({ data }) => {
              const result = data?.createCoachAvailabilityBlockTime
              return {
                ok: !!result?.success,
                message: result?.message,
              }
            })
            .catch(e => ({ ok: false, message: e.message }))
        }),
      )

      const failures = settled.filter(s => !s.ok)
      if (failures.length === 0) {
        enqueueSnackbar(
          settled.length > 1
            ? `${settled.length} blocked times saved`
            : 'Blocked time saved',
          { variant: 'success' },
        )
        onSuccess && onSuccess()
      } else if (failures.length === settled.length) {
        enqueueSnackbar(failures[0].message || 'Failed to save', {
          variant: 'error',
        })
      } else {
        enqueueSnackbar(
          `Saved ${settled.length - failures.length} of ${
            settled.length
          } blocked times; ${failures.length} failed`,
          { variant: 'warning' },
        )
        onSuccess && onSuccess()
      }
    } finally {
      setIsBatchSubmittingBlocks(false)
    }
  }

  const datePickerSlotProps = {
    textField: {
      sx: outlinedPickerFieldSx,
      variant: 'outlined',
      fullWidth: true,
    },
    openPickerButton: {
      sx: { color: PICKER_ICON_COLOR },
    },
    popper: { sx: pickerPopperSx },
  }

  const timePickerSlotProps = {
    textField: {
      sx: outlinedPickerFieldSx,
      variant: 'outlined',
      fullWidth: true,
    },
    openPickerButton: {
      sx: { color: PICKER_ICON_COLOR },
    },
  }

  return (
    <ThemeProvider theme={calendarDatePickerTheme}>
      <LocalizationProvider dateAdapter={CustomLuxonAdapter} locale="en-us">
        {type === 'open' && facilities && facilities.length > 0 && (
          <Box mb={3}>
            <Typography variant="body2" className={classes.sectionLabel}>
              Where do you want to add it?
            </Typography>
            <FormControl
              variant="outlined"
              fullWidth
              className={classes.facilityField}
            >
              {facilities.length === 1 ? (
                <TextField
                  disabled
                  value={facilities[0].name}
                  variant="outlined"
                  inputProps={{ 'aria-label': 'Facility' }}
                />
              ) : (
                <TextField
                  select
                  value={selectedFacilityId}
                  variant="outlined"
                  label="Facility"
                  onChange={e => setSelectedFacilityId(e.target.value)}
                  inputProps={{ 'aria-label': 'Facility' }}
                  SelectProps={{
                    MenuProps: {
                      classes: { paper: classes.facilityMenuPaper },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                    },
                  }}
                >
                  {facilities.map(f => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </FormControl>
          </Box>
        )}

        {isBlock && (
          <Box className={classes.tip} role="status">
            <TipWell
              icon={InfoOutlinedIcon}
              caption={formatBlockingTip(ranges)}
            />
          </Box>
        )}

        <Box mb={3}>
          <Typography variant="body2" className={classes.sectionLabel}>
            Date and Time
          </Typography>
          {ranges.map((r, idx) => {
            const invalid = isInvalidRange(r)
            const showDatePicker = isBlock || idx === 0
            return (
              <Box key={idx} className={classes.rangeBlock}>
                {showDatePicker ? (
                  <Box className={classes.dateRow}>
                    <DesktopDatePicker
                      label="Pick a date"
                      value={isBlock ? r.date : date}
                      format="MMM d, yyyy"
                      onChange={val => {
                        const next = toValidOrNull(val)
                        if (isBlock) {
                          updateRange(idx, 'date', next)
                        } else {
                          setDate(next)
                        }
                      }}
                      disablePast
                      dayOfWeekFormatter={day => `${day}`}
                      closeOnSelect
                      slots={mergeDesktopDatePickerSlots({
                        openPickerIcon: DatePickerIcon,
                      })}
                      slotProps={mergeDesktopDatePickerSlotProps(
                        datePickerSlotProps,
                      )}
                    />
                  </Box>
                ) : null}
                <Box className={classes.timeRow}>
                  <Box className={classes.timeField}>
                    <DesktopTimePicker
                      label="From"
                      value={r.startTime}
                      minutesStep={5}
                      closeOnSelect={false}
                      views={['hours']}
                      format={ENTER_TIME_FIELD_FORMAT}
                      viewRenderers={enterTimeViewRenderers}
                      onChange={val =>
                        updateRange(idx, 'startTime', toValidOrNull(val))
                      }
                      slots={{ openPickerIcon: TimePickerIcon }}
                      slotProps={mergeEnterTimePickerSlotProps({
                        ...timePickerSlotProps,
                        textField: {
                          ...timePickerSlotProps.textField,
                          error: invalid,
                          inputProps: {
                            'data-testid': `time-picker-from-${idx}`,
                          },
                        },
                      })}
                    />
                  </Box>
                  <Box className={classes.timeField}>
                    <DesktopTimePicker
                      label="To"
                      value={r.endTime}
                      minutesStep={5}
                      closeOnSelect={false}
                      views={['hours']}
                      format={ENTER_TIME_FIELD_FORMAT}
                      viewRenderers={enterTimeViewRenderers}
                      onChange={val =>
                        updateRange(idx, 'endTime', toValidOrNull(val))
                      }
                      slots={{ openPickerIcon: TimePickerIcon }}
                      slotProps={mergeEnterTimePickerSlotProps({
                        ...timePickerSlotProps,
                        textField: {
                          ...timePickerSlotProps.textField,
                          error: invalid,
                          inputProps: {
                            'data-testid': `time-picker-to-${idx}`,
                          },
                        },
                      })}
                    />
                  </Box>
                  {ranges.length > 1 ? (
                    <Box className={classes.timeRangeRemoveSlot}>
                      <IconButton
                        size="small"
                        aria-label="remove time range"
                        onClick={() => removeRange(idx)}
                      >
                        <CloseOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : null}
                </Box>
                {invalid && (
                  <FormHelperText error>
                    End time must be after start time
                  </FormHelperText>
                )}
              </Box>
            )
          })}
          <Button
            color="primary"
            startIcon={<AddOutlinedIcon />}
            className={classes.addRangeButton}
            onClick={addRange}
          >
            Add Another Time Range
          </Button>
        </Box>

        <Box mb={1} className={classes.noteField}>
          <TextField
            variant="outlined"
            fullWidth
            label="Note (optional)"
            placeholder="e.g. Virtual session, Tournament prep"
            value={note}
            onChange={e => setNote(e.target.value)}
            inputProps={{ maxLength: 60 }}
          />
        </Box>

        <Box className={classes.actions}>
          {onCancel && (
            <Button className={classes.cancelAction} onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            className={classes.saveAction}
            disabled={!canSubmit}
            onClick={onSubmit}
            startIcon={<AddOutlinedIcon />}
          >
            Add Time
          </Button>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

export default OneTimeSlotForm

export { formatBlockingTip, formatRangeLabel }
